/**
 * Pure URL helpers for the Component Editor iframe.
 *
 * Kept dependency-free (no React/DOM) so they can be unit-tested directly.
 */

/**
 * Pick the base URL to load in the editor iframe.
 *
 * Prefers the canonical post-redirect URL resolved by the backend embed probe
 * (`finalUrl`) when present; otherwise falls back to the stored page URL. This
 * is the feature-detection seam: an older backend that doesn't return a
 * `finalUrl` (so `resolvedUrl` is null/undefined) keeps the legacy behavior.
 */
export function selectIframeBaseUrl(
  resolvedUrl: string | null | undefined,
  pageUrl: string,
): string {
  return resolvedUrl ?? pageUrl;
}

/**
 * Same-site origin set for a stored origin: the exact origin, its `www.`
 * toggle, and the `https` upgrade of an `http` origin.
 *
 * Mirrors `computeAllowedOrigins` in `@humblytics/editor-core` (kept as a small
 * self-contained copy so this module has no React/package imports and stays
 * unit-testable in a node environment). Keep the two in sync.
 */
function sameSiteOrigins(origin: string): Set<string> {
  const set = new Set<string>();
  const add = (candidate: string) => {
    try {
      const url = new URL(candidate);
      set.add(url.origin);
      const host = url.hostname;
      const sibling = host.startsWith("www.") ? host.slice(4) : `www.${host}`;
      const siblingUrl = new URL(url.origin);
      siblingUrl.hostname = sibling;
      set.add(siblingUrl.origin);
    } catch {
      // ignore non-URL input
    }
  };
  add(origin);
  try {
    const url = new URL(origin);
    if (url.protocol === "http:") {
      url.protocol = "https:";
      add(url.origin);
    }
  } catch {
    // ignore
  }
  return set;
}

/**
 * Security gate for the backend-resolved canonical URL (`finalUrl`).
 *
 * We only adopt a `finalUrl` when it is http(s) AND stays within the stored
 * page's same-site origin set (apex↔www / http→https). The editor iframe URL
 * carries the session `authToken` in its query string, and the parent bridge
 * derives the origin it trusts for `postMessage` from this URL — so a
 * `finalUrl` that pointed at a different origin (open redirect, or a
 * cross-domain "canonical" move) would hand the token to that origin and let
 * it drive the editor. SSRF / redirect-following safety on the probe itself is
 * the backend's responsibility; this is the independent client-side backstop
 * and is intentionally stricter than the contract's "cross-domain allowed"
 * note. Cross-domain resolutions are ignored (we keep the stored URL).
 */
export function isTrustedResolvedUrl(
  finalUrl: string,
  storedUrl: string,
): boolean {
  try {
    const resolved = new URL(finalUrl);
    if (resolved.protocol !== "http:" && resolved.protocol !== "https:") {
      return false;
    }
    const stored = new URL(storedUrl);
    return sameSiteOrigins(stored.origin).has(resolved.origin);
  } catch {
    return false;
  }
}

/**
 * Normalise a site domain candidate to an `https://host` base, or null when it
 * is not a hostname. The layout's `currentDomain` falls back to the workspace
 * or property *name* ("amirmxt") and, in aggregate mode, to a comma-joined
 * domain list, so a candidate must have a dot and no spaces or commas.
 */
export function toSiteBase(domain: string | null | undefined): string | null {
  const raw = (domain || "").trim();
  if (!raw || /[\s,]/.test(raw)) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    if (!url.hostname.includes(".") && url.hostname !== "localhost") return null;
    return url.origin;
  } catch {
    return null;
  }
}

/**
 * A stored path ("/pricing") becomes a full URL on the first candidate domain
 * that is a real hostname. Absolute http(s) URLs pass through unchanged.
 */
export function toAbsolutePageUrl(
  pageUrl: string,
  domainCandidates: Array<string | null | undefined>,
): string | null {
  if (/^https?:\/\//i.test(pageUrl)) return pageUrl;
  for (const candidate of domainCandidates) {
    const base = toSiteBase(candidate);
    if (base) return `${base}${pageUrl.startsWith("/") ? "" : "/"}${pageUrl}`;
  }
  return null;
}
